import React from 'react';
import { Link } from 'react-router-dom';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from '../Education/ScienceOfTime.module.scss';
import { tokens } from '../../../styles/tokens';

const linkStyle = { color: tokens.skyBlue, textDecoration: 'none', fontWeight: 600 } as const;
const linkStylePlain = { color: tokens.skyBlue, textDecoration: 'none' } as const;

export const BlogPostRoutineEngine: React.FC = () => {
  return (
    <StaticPageLayout>
      <div className={styles.container}>
        <div style={{ marginBottom: '24px' }}>
          <Link to="/blog" style={linkStyle}>
            ← Back to Blog
          </Link>
        </div>

        <div className={styles.header}>
          <h1>The Routine Engine</h1>
          <p className={styles.subtitle}>
            It’s not discipline. It’s not saying things louder. It’s predictable sequences your child’s brain can run on autopilot.
          </p>
        </div>

        <section className={styles.section}>
          <p>
            If <Link to="/blog/abstract-time" style={linkStyle}>Blog 1</Link> was about why "5 minutes" fails, this one is about what actually makes mornings, exits, and transitions faster over time.
          </p>
          <p>Short answer: It’s routines.</p>
          <p>
            And not the rigid, military kind—but predictable sequences your child’s brain can run on autopilot.
          </p>
        </section>

        <section className={styles.section}>
          <h2>Why Transitions Are So Hard for Kids</h2>
          <p>
            From an adult perspective, leaving the house feels like a single task. From a child’s perspective, it’s a stack of micro-transitions:
          </p>
          <ul className={styles.tipsList}>
            <li>Stop playing</li>
            <li>Shift attention</li>
            <li>Change clothes</li>
            <li>Find shoes</li>
            <li>Tolerate urgency</li>
            <li>Move environments</li>
          </ul>
          <p>
            Each of those requires self-regulation, and young children are still developing that skill. Research across developmental psychology consistently shows that children handle transitions better when the sequence is predictable, the order doesn't change, and expectations are clear.
          </p>
        </section>

        <section className={styles.section}>
          <h2>What Routines Actually Do (Scientifically)</h2>
          
          <div className={styles.quoteBox}>
            <p>“Out of 4297 initial hits, 170 studies met the selection criteria.”</p>
            <span className={styles.source}>— Wiley Online Library</span>
          </div>

          <p>
            A large body of research—including recent systematic reviews—shows that consistent family routines are linked to better self-regulation, fewer power struggles, and stronger executive functioning.
          </p>

          <div className={styles.quoteBox}>
            <p>“Family routines provide both a predictable structure to guide behavior and an emotional environment that supports development.”</p>
            <span className={styles.source}>— SpringerLink</span>
          </div>
          
          <div className={styles.statCard} style={{ margin: '32px 0' }}>
            <p style={{ fontStyle: 'italic', color: tokens.charcoal }}>
              Why? Because routines remove decision-making.
            </p>
          </div>

          <p>
            When a child knows <strong>"After breakfast → shoes → coat → door"</strong>, they don't have to ask "what now?" or negotiate "do I really have to?". The routine decides for them. This frees up mental energy—and that’s why routines make kids faster without pressure.
          </p>
        </section>

        <section className={styles.section}>
          <h2>Why "Just Hurry Up" Backfires</h2>
          <p>
            When routines aren’t stable, parents compensate with reminders like "Come on" or "We're late".
          </p>
          <p>
            From the child’s side, this feels like unpredictability and rising tension. Research shows that inconsistent routines increase anxiety and resistance. The child doesn’t know which step matters most, so they stall.
          </p>

          <div className={styles.quoteBox}>
            <p>“Naturally occurring family routines and meaningful rituals provide both a predictable structure that guides behavior and an emotional climate that supports early development.”</p>
            <span className={styles.source}>— Arizona Department of Education</span>
          </div>
        </section>

        <section className={styles.section}>
          <h2>What a Good "Leaving-the-House" Routine Looks Like</h2>
          <p>Effective routines share a few properties:</p>
          
          <div style={{ background: 'white', padding: '32px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', marginTop: '24px' }}>
            <h3 style={{ marginTop: 0, fontSize: '1.4rem' }}>1. Short</h3>
            <p>5–7 steps max. Example: Finish current activity → Shoes → Coat → Bag → Door.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>2. Always in the Same Order</h3>
            <p>Children learn sequences faster than schedules. Changing the order creates friction.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>3. Anchored to Place</h3>
            <p>Shoes always by the door. Timer always visible in the same spot. Environment is part of the routine.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>4. Predictable, Not Negotiable</h3>
            <p>You don't ask "Are you ready?". You state "This is the leaving routine." Predictability reduces power struggles.</p>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Where Visual Time Fits In</h2>
          <p>
            Routines alone help. Routines + visible time help much more.
          </p>
          <ul className={styles.tipsList}>
            <li>The routine defines <strong>what</strong> happens</li>
            <li>Visual time defines <strong>when</strong> it changes</li>
          </ul>
          <p>
            A wedge-based visual timer becomes the bridge between "I'm playing" and "We're leaving." Instead of the parent interrupting, time itself does.
          </p>
        </section>

        <section className={styles.section}>
          <h2>The Most Common Routine Mistake</h2>
          <p>
            <strong>Parents announce the routine before they’re actually ready.</strong>
          </p>
          <p>
            If you say "Time to go" but then look for keys or check your phone, you break the child's momentum. Research on transitions is clear: waiting creates disengagement.
          </p>
          <p>
            <strong>Rule of thumb:</strong> The routine starts only when you are ready to walk out the door.
          </p>
        </section>

        <section className={styles.section}>
          <h2>The Takeaway</h2>
          <p>
            If <Link to="/blog/abstract-time" style={linkStyle}>Blog 1</Link> was about making time visible, this blog is about making life predictable.
          </p>
          <p>
            Routines are the engine. Visual time is the dashboard. Together, they cut transition time, reduce conflict, and help kids build real time awareness.
          </p>
          <p style={{ marginTop: '24px', fontWeight: 600 }}>
            Up next: <Link to="/blog/visual-tools" style={linkStylePlain}>Why visual timers work—and how to use them without creating stress.</Link>
          </p>
        </section>

        <div className={styles.cta}>
          <h3>Build your engine.</h3>
          <Link to="/register" className={styles.primaryButton}>
            Create Your Visual Routine
          </Link>
        </div>

        <div className={styles.sourcesSection}>
          <h3>Sources</h3>
          
          <div className={styles.sourceItem}>
            <p>Syed et al. (2024). Routines and child development: A systematic review. Journal of Family Theory & Review.</p>
            <a href="https://onlinelibrary.wiley.com/doi/pdf/10.1111/jftr.12549" target="_blank" rel="noopener noreferrer">
              https://onlinelibrary.wiley.com/doi/pdf/10.1111/jftr.12549
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>Spagnola, M. & Fiese, B.H. (2007). Family Routines and Rituals: A Context for Development in the Lives of Young Children.</p>
            <a href="https://www.azed.gov/sites/default/files/2019/01/Family_Routines_and_Rituals__A_Context_for.2%20%281%29.pdf" target="_blank" rel="noopener noreferrer">
              https://www.azed.gov/sites/default/files/2019/01/Family_Routines_and_Rituals__A_Context_for.2%20%281%29.pdf
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>Wang et al. (2023). Associations between Family Routines, Family Relationships, and Elementary School Children’s Behavior. Journal of Child and Family Studies.</p>
            <a href="https://link.springer.com/article/10.1007/s10826-023-02687-w" target="_blank" rel="noopener noreferrer">
              https://link.springer.com/article/10.1007/s10826-023-02687-w
            </a>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};
